import type {ReactNode} from 'react'
import flatten from 'lodash/flatten'
import range from 'lodash/range'
import zip from 'lodash/zip'
import {
  addMaybeClassName,
  addMaybeClassNames
} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {
  displayHTMLOrPlainTextInSpan,
  toHTMLTextDangerous
} from '@freckle/educator-entities/ts/common/models/html-text'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {breadcrumbs, noWrapEllipses, separator, breadcrumbsLeaf} from './breadcrumbs.module.scss'

/** Plain text to display as a breadcrumb item (for a parent, not the current item) */
type ParentBreadcrumbNameT = string

/** Text to display as the last breadcrumb item (representing the current page) */
type CurrentBreadcrumbNameT = ReactNode

export type BreadcrumbItemT = {
  url: string
  name: ParentBreadcrumbNameT
  className?: string
}

export type BreadcrumbsProps = {
  path: Array<BreadcrumbItemT>
  currentItemName: CurrentBreadcrumbNameT
  currentItemClass?: string
  enableNoWrapEllipses?: boolean
  breadcrumbsClass?: string
  reactRouter?: boolean
}

export function Breadcrumbs({
  path,
  currentItemName,
  enableNoWrapEllipses = false,
  currentItemClass,
  breadcrumbsClass,
  reactRouter = true
}: BreadcrumbsProps) {
  const className = addMaybeClassNames(breadcrumbs, [
    enableNoWrapEllipses ? ` ${noWrapEllipses}` : null,
    breadcrumbsClass
  ])

  return (
    <div className={className} data-test="breadcrumbs">
      {renderParentList({path, reactRouter})}
      {renderCurrentBreadcrumbItem({currentItemName, currentItemClass})}
    </div>
  )
}

function renderParentList(props: {path: Array<BreadcrumbItemT>; reactRouter: boolean}): ReactNode {
  const {path, reactRouter} = props
  const pathList: Array<ReactNode> = path.map((breadcrumbItem, index: number) =>
    renderParentBreadcrumbItem({...breadcrumbItem, index, reactRouter})
  )
  const separators: Array<ReactNode> = range(0, pathList.length).map(i => {
    return (
      <span key={`separator-${i}`} className={separator}>
        &#47;
      </span>
    )
  })

  return flatten(zip(pathList, separators) as Array<Array<ReactNode>>)
}

function renderParentBreadcrumbItem(
  props: BreadcrumbItemT & {index: number; reactRouter: boolean}
) {
  const {url, className, name, index, reactRouter} = props
  const linkProps = {
    href: url,
    className,
    reactRouter,
    dataTest: `breadcrumb-link-${index}`
  }
  return (
    <Link key={`root-${index}`} {...linkProps}>
      {displayHTMLOrPlainTextInSpan(toHTMLTextDangerous(name))}
    </Link>
  )
}

function renderCurrentBreadcrumbItem(props: {
  currentItemName: CurrentBreadcrumbNameT
  currentItemClass: string | undefined
}) {
  const {currentItemName, currentItemClass} = props
  const currentItemClassName = addMaybeClassName(breadcrumbsLeaf, currentItemClass)
  const spanProps = {className: currentItemClassName}
  return <span {...spanProps}>{currentItemName}</span>
}
